import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Building2, CalendarDays, FilterX, UserRoundCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { EmptyState } from "@/components/common/EmptyState";
import { EmploymentStatusBadge } from "@/pages/shared/EmploymentList";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/constants";

// Phase 35 (supersedes Phase 34): persistent placement record. Every candidate
// this recruiter has placed stays here for the entire lifecycle:
// Joined -> Invoice Pending (automatic on lock-end date) -> Payment Received,
// or Left. Purely informational — recruiter actions live in Joining Confirmation.

export const LIFECYCLE_STATUSES = ["Joined", "Invoice Pending", "Payment Received", "Left"];

const NextCheckIn = ({ e }) => {
  if (e.status !== "Joined" || !e.next_due_date) return <span className="text-muted-foreground">—</span>;
  const d = e.days_until_next_due;
  const tone = d <= 0
    ? "bg-red-50 text-red-700 border-red-200"
    : d <= 3
      ? "bg-amber-50 text-amber-700 border-amber-200"
      : "bg-emerald-50 text-emerald-700 border-emerald-200";
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-muted-foreground text-xs">{e.next_due_type}</span>
      <Badge variant="outline" className={`rounded-full font-medium whitespace-nowrap ${tone}`} data-testid="joined-next-checkin-badge">
        {d <= 0 ? "due now" : d === 1 ? "in 1 day" : `in ${d} days`}
      </Badge>
    </div>
  );
};

export default function RecruiterJoined() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [month, setMonth] = useState("");
  const [companyId, setCompanyId] = useState("all");
  const [status, setStatus] = useState("all");

  const load = useCallback(() => {
    setError(false);
    const params = {};
    if (month) params.month = month;
    if (companyId !== "all") params.company_id = companyId;
    if (status !== "all") params.status = status;
    api.get("/employment/joined-window", { params })
      .then((res) => setItems(res.data || []))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [month, companyId, status]);

  useEffect(() => { load(); }, [load]);

  // Company filter options derived from the recruiter's own placements
  const [companyOptions, setCompanyOptions] = useState([]);
  useEffect(() => {
    api.get("/employment/joined-window")
      .then((res) => {
        const seen = new Map();
        (res.data || []).forEach((e) => {
          if (e.company?.id && !seen.has(e.company.id)) seen.set(e.company.id, e.company.name);
        });
        setCompanyOptions([...seen.entries()].map(([id, name]) => ({ id, name })));
      })
      .catch(() => {});
  }, []);

  const hasFilters = month || companyId !== "all" || status !== "all";
  const clearFilters = () => { setMonth(""); setCompanyId("all"); setStatus("all"); };

  if (loading) {
    return <div className="space-y-3"><Skeleton className="h-10 w-64 rounded-lg" />{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-14 rounded-xl" />)}</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-semibold tracking-tight">Joined</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Your permanent placement record — every candidate you placed, across the whole lifecycle: Joined, Invoice Pending, Payment Received, or Left.
        </p>
      </div>

      {/* Combinable filters: month of joining + company + status */}
      <Card className="rounded-xl border-border shadow-none p-3">
        <div className="flex flex-wrap items-center gap-2">
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
            className="w-[170px] bg-white rounded-lg" aria-label="Month of joining" data-testid="joined-filter-month" />
          <Select value={companyId} onValueChange={setCompanyId}>
            <SelectTrigger className="w-[200px] bg-white rounded-lg" data-testid="joined-filter-company">
              <SelectValue placeholder="Company" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All companies</SelectItem>
              {companyOptions.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[190px] bg-white rounded-lg" data-testid="joined-filter-status">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {LIFECYCLE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          {hasFilters && (
            <Button variant="ghost" size="sm" className="rounded-lg" onClick={clearFilters} data-testid="joined-filter-clear">
              <FilterX className="h-3.5 w-3.5 mr-1.5" /> Clear
            </Button>
          )}
          <span className="ml-auto text-xs text-muted-foreground" data-testid="joined-count">{items.length} placement{items.length === 1 ? "" : "s"}</span>
        </div>
      </Card>

      {error ? (
        <Card className="rounded-xl border-border shadow-none p-10 flex flex-col items-center gap-3" data-testid="joined-error">
          <p className="text-sm text-muted-foreground">Could not load placements.</p>
          <Button variant="outline" size="sm" className="rounded-lg bg-white" onClick={load}>Retry</Button>
        </Card>
      ) : items.length === 0 ? (
        <EmptyState icon={UserRoundCheck}
          title={hasFilters ? "No placements match these filters" : "No placements yet"}
          description={hasFilters ? "Try clearing or changing the filters above." : "Candidates you place appear here permanently, month by month."} />
      ) : (
        <Card className="rounded-xl border-border shadow-none overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Candidate</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead>Date of joining</TableHead>
                  <TableHead>Lock ends</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Next check-in</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((e) => (
                  <TableRow key={e.id} className="hover:bg-muted/60" data-testid="joined-row">
                    <TableCell>
                      <Link to={`/recruiter/employment/${e.id}`} className="hover:underline" data-testid={`joined-link-${e.id}`}>
                        <p className="font-medium text-primary">{e.candidate?.full_name || "—"}</p>
                      </Link>
                      <p className="text-xs text-muted-foreground font-mono">{e.candidate?.code}</p>
                    </TableCell>
                    <TableCell className="text-sm">
                      <span className="inline-flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-muted-foreground" />{e.company?.name || "—"}</span>
                    </TableCell>
                    <TableCell className="text-sm">{e.job?.title || "—"}</TableCell>
                    <TableCell className="text-sm">
                      <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />{formatDate(e.date_of_joining)}</span>
                    </TableCell>
                    <TableCell className="text-sm">{formatDate(e.locking_end_date)}</TableCell>
                    <TableCell><EmploymentStatusBadge status={e.status} className="text-[11px]" /></TableCell>
                    <TableCell><NextCheckIn e={e} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}
    </div>
  );
}
